import { http } from "../http-common";
import { PageableResponse } from "../types/pagination";

export interface CompanyNonconformitySummary {
    hasPreparedReports: boolean;
}

export interface CompanyNonconformityDistinctiveAnswer {
    questionId?: number | null;
    questionTitle?: string | null;
    answerText?: string | null;
    questionSortOrder?: number | null;
}

export interface CompanyNonconformityRow {
    proposalId?: number | null;
    proposalNumber?: string | null;
    proposalCreatedAt?: string | null;
    responseId?: number | null;
    taskId?: number | null;
    appointmentId?: number | null;
    accountId?: number | null;
    reportNumber?: string | null;
    reportSequenceNo?: number | null;
    responseUpdatedAt?: string | null;
    attributeId?: number | null;
    attributeName?: string | null;
    controlAreaName?: string | null;
    questionId?: number | null;
    questionTitle?: string | null;
    questionSortOrder?: number | null;
    nodeId?: number | null;
    nodeTitle?: string | null;
    nodeSortOrder?: number | null;
    answerTitle?: string | null;
    description?: string | null;
    distinctiveAnswers?: string | null;
    distinctiveAnswerItems?: CompanyNonconformityDistinctiveAnswer[];
    documentId?: number | null;
    imageUrl?: string | null;
    imageName?: string | null;
    imageType?: string | null;
    inspectorName?: string | null;
    orderNo?: number | null;
    controlCompleted?: boolean | null;
    reportApproved?: boolean | null;
    correctionCaseId?: number | null;
    correctionStatus?: string | null;
    hasOpenCorrection?: boolean | null;
    followUpCaseId?: number | null;
    followUpStatus?: string | null;
    hasOpenFollowUp?: boolean | null;
}

export interface CompanyNonconformityGroup {
    groupKey: string;
    proposalId?: number | null;
    proposalNumber?: string | null;
    proposalCreatedAt?: string | null;
    responseId?: number | null;
    taskId?: number | null;
    appointmentId?: number | null;
    accountId?: number | null;
    reportNumber?: string | null;
    reportSequenceNo?: number | null;
    responseUpdatedAt?: string | null;
    attributeId?: number | null;
    attributeName?: string | null;
    controlAreaName?: string | null;
    distinctiveAnswers?: string | null;
    distinctiveAnswerItems?: CompanyNonconformityDistinctiveAnswer[];
    inspectorName?: string | null;
    orderNo?: number | null;
    nonconformityCount?: number | null;
    controlCompleted?: boolean | null;
    reportApproved?: boolean | null;
    correctionCaseId?: number | null;
    correctionStatus?: string | null;
    hasOpenCorrection?: boolean | null;
    followUpCaseId?: number | null;
    followUpStatus?: string | null;
    hasOpenFollowUp?: boolean | null;
    items?: CompanyNonconformityRow[];
}

export interface CompanyControlReportGroup {
    groupKey: string;
    proposalId?: number | null;
    proposalNumber?: string | null;
    proposalCreatedAt?: string | null;
    responseId?: number | null;
    taskId?: number | null;
    appointmentId?: number | null;
    accountId?: number | null;
    reportNumber?: string | null;
    reportSequenceNo?: number | null;
    responseUpdatedAt?: string | null;
    attributeId?: number | null;
    attributeName?: string | null;
    controlAreaName?: string | null;
    distinctiveAnswers?: string | null;
    distinctiveAnswerItems?: CompanyNonconformityDistinctiveAnswer[];
    inspectorName?: string | null;
    orderNo?: number | null;
    controlCompleted?: boolean | null;
    reportApproved?: boolean | null;
    bulkGroupId?: number | null;
    bulkReportCount?: number | null;
    savedResponseCount?: number | null;
    isBulkReport?: boolean | null;
}

export interface CompanyServiceHistoryItem {
    taskId?: number | null;
    proposalId?: number | null;
    attributeId?: number | null;
    attributeName?: string | null;
    quantity?: number | null;
    controlDate?: string | null;
    nextControlDate?: string | null;
}

export interface CompanyServiceHistoryGroup {
    groupKey: string;
    proposalId?: number | null;
    proposalNumber?: string | null;
    services?: CompanyServiceHistoryItem[];
}

export interface CompanyServiceListItem {
    reportAttributeId?: number | null;
    proposalId?: number | null;
    attributeId?: number | null;
    attributeName?: string | null;
    quantity?: number | null;
    controlDate?: string | null;
    nextControlDate?: string | null;
}

export interface CompanyServiceListGroup {
    groupKey: string;
    proposalId?: number | null;
    proposalNumber?: string | null;
    services?: CompanyServiceListItem[];
}

export interface CompanyNonconformityPlan {
    correctionCaseId?: number | null;
    caseType?: 'CORRECTION' | 'FOLLOW_UP' | string | null;
    status?: string | null;
    proposalId?: number | null;
    proposalNumber?: string | null;
    proposalCreatedAt?: string | null;
    sourceResponseId?: number | null;
    sourceTaskId?: number | null;
    sourceAttributeId?: number | null;
    sourceAttributeName?: string | null;
    sourceGroupId?: number | null;
    sourceGroupName?: string | null;
    sourceCountable?: boolean | null;
    sourceFieldControlTime?: number | string | null;
    sourceOfferingName?: string | null;
    sourceOrderNo?: number | null;
    sourceReportNumber?: string | null;
    sourceReportSequenceNo?: number | null;
    appointmentId?: number | null;
    correctionTaskId?: number | null;
    correctionResponseId?: number | null;
    assignedAccountId?: number | null;
    assignedInspectorName?: string | null;
    plannedStartDate?: string | null;
    plannedEndDate?: string | null;
    contractId?: string | null;
    contractUrl?: string | null;
    isCompanyProcessDone?: boolean | null;
    isInspectorProcessDone?: boolean | null;
    questionCount?: number | null;
}

const getAllMe = (params: any = { sortField: 'createdAt', sortDirection: 'DESC' }) => {
    return http.get<any>(`/companies/me`, {
        params: {
            name: params.search || '',
            city: params.city || '',
            district: params.district || '',
            page: params.page || 0,
            size: params.size || 10,
            sort: params.sortField ?? '',
            direction: params.sortDirection ?? ''
        }
    });
};

const getAll = (params: any = { sortField: 'createdAt', sortDirection: 'DESC' }) => {
    return http.get<any>(`/companies`, {
        params: {
            name: params.search || '',
            city: params.city || '',
            district: params.district || '',
            accountId: params.accountId || '',
            page: params.page || 0,
            size: params.size || 10,
            sort: params.sortField ?? '',
            direction: params.sortDirection ?? ''
        }
    });
};

const getCompanyAll = () => {
    return http.get<any>(`/companies/all`);
};

const getCompany = (id: any) => {
    return http.get<any>(`/companies/${id}`);
};

const create = (data: any) => {
    return http.post<any>(`/companies`, data);
};

const remove = (id: any) => {
    return http.delete<any>(`/companies/${id}`);
};

const update = (id: any, data: any) => {
    return http.put<any>(`/companies/${id}`, data);
};

const postImage = (data: FormData, code: string) => {
    return http.post<any>(`/images`, data, {
        params: { code },
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
};

const getStates = (id: any) => {
    return http.get<any>(`/countries/${id}/states`);
};

const getDistricts = (stateId: any) => {
    return http.get<any>(`/states/${stateId}/districts`);
};

const getImage = (id: any) => {
    return http.get<any>(`/images/${id}`);
};

const removeAddress = (id: any) => {
    return http.delete<any>(`/address-properties/${id}`);
};

const removeAuthorizedPerson = (id: any) => {
    return http.delete<any>(`/authorized-persons/${id}`);
};

const transfer = (companyId: any, accountId: any) => {
    return http.post<any>(`/companies/transfer`, {
        companyId: companyId,
        newAccountId: accountId
    });
};

const getNonconformitySummary = (companyId: any) => {
    return http.get<CompanyNonconformitySummary>(`/companies/${companyId}/nonconformities/summary`);
};

const getNonconformities = (companyId: any, params?: any) => {
    return http.get<PageableResponse<CompanyNonconformityRow>>(`/companies/${companyId}/nonconformities`, {
        params: {
            page: params?.page ?? 0,
            size: params?.size ?? 20,
            ...(params || {}),
        },
    });
};

const getNonconformityGroups = (companyId: any, params?: any) => {
    return http.get<PageableResponse<CompanyNonconformityGroup>>(`/companies/${companyId}/nonconformities/groups`, {
        params: {
            page: params?.page ?? 0,
            size: params?.size ?? 20,
            ...(params || {}),
        },
    });
};

const getNonconformityPlans = (companyId: any, params?: any) => {
    return http.get<CompanyNonconformityPlan[]>(`/companies/${companyId}/nonconformities/plans`, {
        params: {
            ...(params || {}),
        },
    });
};

const getControlReportGroups = (companyId: any, params?: any) => {
    return http.get<PageableResponse<CompanyControlReportGroup>>(`/companies/${companyId}/control-reports`, {
        params: {
            page: params?.page ?? 0,
            size: params?.size ?? 20,
            ...(params || {}),
        },
    });
};

const getServiceHistoryGroups = (companyId: any, params?: any) => {
    return http.get<PageableResponse<CompanyServiceHistoryGroup>>(`/companies/${companyId}/services`, {
        params: {
            page: params?.page ?? 0,
            size: params?.size ?? 10,
            ...(params || {}),
        },
    });
};

const exportServiceHistoryGroups = (companyId: any, params?: any) => {
    return http.get<CompanyServiceHistoryGroup[]>(`/companies/${companyId}/services/export`, {
        params: {
            ...(params || {}),
        },
    });
};

const getServiceListGroups = (companyId: any, params?: any) => {
    return http.get<PageableResponse<CompanyServiceListGroup>>(`/companies/${companyId}/service-list`, {
        params: {
            page: params?.page ?? 0,
            size: params?.size ?? 10,
            ...(params || {}),
        },
    });
};

const exportServiceListGroups = (companyId: any, params?: any) => {
    return http.get<CompanyServiceListGroup[]>(`/companies/${companyId}/service-list/export`, {
        params: {
            ...(params || {}),
        },
    });
};

const downloadNonconformityReportPdf = (companyId: any, params?: any) => {
    return http.get<Blob>(`/companies/${companyId}/nonconformities/report.pdf`, {
        params: {
            ...(params || {}),
        },
        responseType: 'blob',
    });
};

const CompanyService = {
    getCompany,
    postImage,
    getAllMe,
    getAll,
    create,
    remove,
    getStates,
    getDistricts,
    getImage,
    update,
    removeAddress,
    removeAuthorizedPerson,
    transfer,
    getCompanyAll,
    getNonconformitySummary,
    getNonconformities,
    getNonconformityGroups,
    getNonconformityPlans,
    getControlReportGroups,
    getServiceHistoryGroups,
    exportServiceHistoryGroups,
    getServiceListGroups,
    exportServiceListGroups,
    downloadNonconformityReportPdf
};

export default CompanyService;
